import { EVENTS } from './constants.js'

export const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000 // 2 heures (7 200 000 ms)
export const INACTIVITY_STORAGE_KEY = 'auth:last_session_activity_timestamp'
const THROTTLE_MS = 5000 // Sauvegarder dans localStorage au maximum toutes les 5 secondes

class InactivityTracker {
  static _lastSavedTime = 0
  static _intervalId = null
  static _context = null
  static _initialized = false
  static _isLoggingOut = false

  /**
   * Récupère le timestamp de la dernière activité
   * @returns {number}
   */
  static getLastActivity() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const item = window.localStorage.getItem(INACTIVITY_STORAGE_KEY)
        return item ? Number(item) || 0 : 0
      }
    } catch (e) {
      console.warn('InactivityTracker: Impossible de lire le localStorage', e)
    }
    return 0
  }

  /**
   * Vérifie si le délai d'inactivité de 2 heures est dépassé
   * @returns {boolean}
   */
  static isExpired() {
    if (typeof window === 'undefined') return false

    // Si on est déjà sur la page de login, la session n'a pas à être vérifiée
    if (window.location.pathname.includes('/login')) return false

    // Vérifier si un utilisateur est connecté
    const hasUser =
      !!window.localStorage.getItem('user:userData') ||
      !!window.localStorage.getItem('userData')
    if (!hasUser) return false

    const lastActivity = this.getLastActivity()

    // Si un utilisateur est connecté mais qu'aucun timestamp d'activité n'est enregistré,
    // la session est considérée comme expirée (session ancienne/précédente).
    if (!lastActivity || lastActivity <= 0) {
      return true
    }

    const elapsed = Date.now() - lastActivity
    return elapsed > INACTIVITY_TIMEOUT_MS
  }

  /**
   * Enregistre l'activité courante de l'utilisateur (throttled)
   */
  static recordActivity() {
    if (typeof window === 'undefined' || this._isLoggingOut) return
    if (window.location.pathname.includes('/login')) return

    const hasUser =
      !!window.localStorage.getItem('user:userData') ||
      !!window.localStorage.getItem('userData')
    if (!hasUser) return

    const now = Date.now()
    if (now - this._lastSavedTime < THROTTLE_MS) return
    this._lastSavedTime = now

    try {
      window.localStorage.setItem(INACTIVITY_STORAGE_KEY, String(now))
    } catch (e) {
      console.warn('InactivityTracker: Impossible d\'écrire dans localStorage', e)
    }
  }

  /**
   * Réinitialise le timestamp d'activité (au moment de la connexion réussie)
   */
  static reset() {
    this._isLoggingOut = false
    const now = Date.now()
    this._lastSavedTime = now
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(INACTIVITY_STORAGE_KEY, String(now))
        window.localStorage.removeItem('auth:last_activity')
      }
    } catch (e) {
      console.warn('InactivityTracker: Impossible d\'écrire dans localStorage', e)
    }
  }

  /**
   * Supprime le timestamp d'activité (au moment de la déconnexion)
   */
  static clear() {
    this._lastSavedTime = 0
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(INACTIVITY_STORAGE_KEY)
        window.localStorage.removeItem('auth:last_activity')
      }
    } catch (e) {
      console.warn('InactivityTracker: Impossible de supprimer du localStorage', e)
    }
  }

  /**
   * Vérifie si l'utilisateur est inactif depuis plus de 2 heures et force la déconnexion
   * @param {object} [context]
   * @returns {boolean} true si l'utilisateur a été déconnecté
   */
  static checkAndEnforce(context) {
    if (typeof window === 'undefined' || this._isLoggingOut) return false

    // Ne pas déclencher si déjà sur /login
    if (window.location.pathname.includes('/login')) return false

    const hasUser =
      !!window.localStorage.getItem('user:userData') ||
      !!window.localStorage.getItem('userData')
    if (!hasUser) return false

    if (this.isExpired()) {
      this._isLoggingOut = true
      console.warn('Déconnexion automatique : inactivité supérieure à 2 heures.')
      this.clear()

      // Nettoyer le localStorage utilisateur immédiatement
      try {
        window.localStorage.removeItem('user:userData')
        window.localStorage.removeItem('userData')
      } catch (e) {}

      const ctx = context || this._context
      if (ctx?.events) {
        ctx.events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
          title: 'Session expirée',
          body: 'Vous avez été déconnecté suite à 2 heures d\'inactivité.',
          type: 'warning',
        })
      }

      // Redirection immédiate vers /login
      window.location.href = '/login'
      return true
    }

    return false
  }

  /**
   * Initialise les écouteurs d'activité utilisateur (souris, clavier, touch, focus, visibility)
   * @param {object} context - Instance AuthService / context
   */
  static init(context) {
    if (typeof window === 'undefined') return
    this._context = context

    // 1. Vérification immédiate
    this.checkAndEnforce(context)

    if (this._initialized) return
    this._initialized = true

    // 2. Événements d'interaction utilisateur
    const userEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
      'wheel',
    ]

    const handleUserInteraction = () => {
      if (this._isLoggingOut || window.location.pathname.includes('/login')) return
      const hasUser =
        !!window.localStorage.getItem('user:userData') ||
        !!window.localStorage.getItem('userData')
      if (hasUser) {
        const wasExpired = this.checkAndEnforce(this._context)
        if (!wasExpired) {
          this.recordActivity()
        }
      }
    }

    userEvents.forEach(eventName => {
      window.addEventListener(eventName, handleUserInteraction, { passive: true })
    })

    // 3. Quand l'utilisateur revient sur l'onglet ou focus la fenêtre
    const handleVisibilityChange = () => {
      if (!document.hidden && !this._isLoggingOut) {
        this.checkAndEnforce(this._context)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', () => {
      if (!this._isLoggingOut) this.checkAndEnforce(this._context)
    })

    // 4. Synchronisation multi-onglets (storage event)
    window.addEventListener('storage', event => {
      if (event.key === INACTIVITY_STORAGE_KEY || event.key === 'user:userData') {
        this.checkAndEnforce(this._context)
      }
    })

    // 5. Intervalle périodique (toutes les 30 secondes)
    if (this._intervalId) clearInterval(this._intervalId)
    this._intervalId = setInterval(() => {
      this.checkAndEnforce(this._context)
    }, 30000)
  }
}

export default InactivityTracker
